import { useEffect } from "react";
import { Navigate, Outlet, Route, Routes } from "react-router-dom";

import LoadingSpinner from "components/atoms/LoadingSpinner/LoadingSpinner";

import { useSelectedBuildingModelCarbonReductionPlan } from "hooks/Building/useBuildingModelCarbonReductionPlan";
import ProtectedRoute from "hooks/useProtectedRoute";
import { ErrorBoundary } from "pages/ErrorState";
import { useFeatureFlagEnabled } from "posthog-js/react";
import BuildingPageBaselineReport from "./BuildingPageBaselineReport";
import BuildingPageSettings from "./BuildingPageSettings";
import DecarbonizationReport from "./DecarbonizationReport/DecarbonizationReport";
import PlanMeasures from "./ProjectReport/ProjectsReport";

import "./BuildingPage.scss";
import BuildingPageHeader from "./BuildingPageHeader/BuildingPageHeader";
import BuildingPageHeaderError from "./BuildingPageHeader/BuildingPageHeaderError";
import BuildingPageSidebars from "./BuildingPageSidebars/BuildingPageSidebars";
import BuildingPageTabs from "./BuildingPageTabs";
import OutcomesV1 from "./OutcomesV1";

function BuildingPageLayout() {
    const {
        bmcrp: selectedBMCRP,
        buildingModel,
        isLoading: isLoadingBMCRP,
    } = useSelectedBuildingModelCarbonReductionPlan();

    useEffect(() => {
        if (buildingModel?.buildingName) {
            document.title = `${buildingModel.buildingName} | Audette`;
        }
        return () => {
            document.title = "Audette";
        };
    }, [buildingModel?.buildingName]);

    if (isLoadingBMCRP || !buildingModel) {
        return <LoadingSpinner />;
    }

    return (
        <div className="building-page-with-sidebar">
            <div className="building-page">
                <ErrorBoundary fallback={<BuildingPageHeaderError buildingModel={buildingModel!} />}>
                    <BuildingPageHeader buildingModel={buildingModel} selectedBMCRP={selectedBMCRP} />
                    <BuildingPageTabs />
                    <div className="report">
                        <Outlet />
                    </div>
                    <BuildingPageSidebars />
                </ErrorBoundary>
            </div>
        </div>
    );
}

function BuildingPage() {
    const outcomesV1Enabled = useFeatureFlagEnabled("new-outcomes-page-for-building");
    const { reportedBMCRP, buildingModel, isLoading: isLoadingBMCRP } = useSelectedBuildingModelCarbonReductionPlan();
    if (isLoadingBMCRP || !buildingModel) {
        return <LoadingSpinner />;
    }

    const reportedPlanPath = `plans/${reportedBMCRP.id}/recommended/baseline`;

    return (
        <ErrorBoundary fallback={<BuildingPageHeaderError buildingModel={buildingModel} />}>
            <div className="report">
                <Routes>
                    <Route path="/:buildingModelUid">
                        <Route index element={<Navigate to={reportedPlanPath} replace />} />
                        <Route
                            path="plans/:buildingModelCarbonReductionPlanId/:planView/"
                            element={<BuildingPageLayout />}
                        >
                            <Route path="settings" element={<BuildingPageSettings />} />
                            <Route index element={<Navigate to="baseline" replace />} />
                            <Route path="baseline" element={<BuildingPageBaselineReport />} />
                            <Route path="outcomes" element={<DecarbonizationReport />} />
                            <Route path="measures" element={<PlanMeasures />} />
                            <Route
                                path="outcomesV1"
                                element={
                                    <ProtectedRoute featureFlagEnabled={!outcomesV1Enabled}>
                                        <OutcomesV1 />
                                    </ProtectedRoute>
                                }
                            />
                        </Route>
                    </Route>
                </Routes>
            </div>
        </ErrorBoundary>
    );
}

export default BuildingPage;
